import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import Sidebar from '../../components/Sidebar';
import { getArticleById, getArticlesByCategory, incrementArticleView } from '../../firebase/firestore';
import { trackVisit } from '../../utils/trackVisit';
import { formatDateTime } from '../../utils/dateHelpers';
import { useLanguage, LANGUAGES } from '../../context/LanguageContext';
import './ArticlePage.css';

export default function ArticlePage() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [article, setArticle] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [activeLang, setActiveLang] = useState(null);

  useEffect(() => {
    setLoading(true);
    getArticleById(id).then(async (a) => {
      if (!a) { setLoading(false); return; }
      setArticle(a);
      try {
        await incrementArticleView(id);
      } catch (e) {
        console.warn('View count update failed:', e);
      }
      trackVisit({ pageType: 'article', pageURL: `/article/${id}`, articleId: id });
      try {
        const rel = await getArticlesByCategory(a.category);
        setRelated(rel.filter(r => r.id !== id).slice(0, 3));
      } catch (e) {
        console.warn('Related articles fetch failed:', e);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  // Pick which language to display:
  // Priority: ?lang= URL param → global language selector → first available
  useEffect(() => {
    if (!article) return;
    const translations = article.translations;
    if (!translations) { setActiveLang(null); return; }

    const params = new URLSearchParams(location.search);
    const urlLang = params.get('lang');

    if (urlLang && translations[urlLang]?.title) {
      setActiveLang(urlLang);
    } else if (language && language !== 'all' && translations[language]?.title) {
      setActiveLang(language);
    } else {
      const first = ['english', 'telugu', 'hindi'].find(l => translations[l]?.title);
      setActiveLang(first || null);
    }
  }, [article, language, location.search]);

  // When user manually switches language tab, update URL ?lang= param
  const handleLangSwitch = (lc) => {
    setActiveLang(lc);
    const params = new URLSearchParams(location.search);
    params.set('lang', lc);
    navigate({ search: params.toString() }, { replace: true });
  };

  const displayTitle = article?.translations?.[activeLang]?.title || article?.title || '';
  const displayContent = article?.translations?.[activeLang]?.content || article?.content || '';
  const availableLangs = article?.translations ? Object.keys(article.translations).filter(l => article.translations[l]?.title) : [];


  // Build share text using the CURRENTLY ACTIVE language title + URL with ?lang= param
  const getShareText = () => {
    const title = displayTitle || article?.title || '';
    const baseUrl = window.location.origin + window.location.pathname;
    const url = activeLang ? `${baseUrl}?lang=${activeLang}` : baseUrl;
    return { title, url };
  };

  const share = (platform) => {
    const { title, url } = getShareText();
    const encodedURL = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(title);
    // Image URL for platforms that accept a direct image param
    const imageURL = article?.thumbnailURL ? encodeURIComponent(article.thumbnailURL) : '';

    const shareText = encodeURIComponent(`${title}\n${url}`);

    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedURL}&quote=${encodedTitle}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedURL}&text=${encodedTitle}`,
      // WhatsApp: sends title + link as a single message; WhatsApp auto-fetches OG image from the link
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(title + '\n' + url)}`,
      // Instagram doesn't have a direct web share URL — we use the Web Share API (mobile) or fall back
      instagram: null,
      // Telegram supports image caption
      telegram: `https://t.me/share/url?url=${encodedURL}&text=${encodedTitle}`,
    };

    if (platform === 'instagram') {
      const { title: igTitle, url: igUrl } = getShareText();
      // Instagram: use Web Share API on mobile
      if (navigator.share) {
        navigator.share({
          title: igTitle,
          text: igTitle,
          url: igUrl,
        }).catch(() => { });
      } else {
        navigator.clipboard.writeText(`${igTitle}\n${igUrl}`);
        alert('Link and title copied! Open Instagram and paste it in your post or story caption.');
        window.open('https://www.instagram.com/', '_blank');
      }
      return;
    }

    // WhatsApp on mobile: use native share so image preview is included
    if (platform === 'whatsapp' && navigator.share) {
      nativeShare();
      return;
    }

    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  const copyLink = async () => {
    const { url } = getShareText();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  // Native Web Share API (works great on mobile — shares image + title + link)
  const nativeShare = async () => {
    if (!navigator.share) return;
    const { title, url } = getShareText();
    try {
      const shareData = {
        title,
        text: title,
        url,
      };

      // Try to include the image as a file if fetch + share files is supported
      if (article?.thumbnailURL && navigator.canShare) {
        try {
          const response = await fetch(article.thumbnailURL);
          const blob = await response.blob();
          const ext = blob.type.split('/')[1] || 'jpg';
          const file = new File([blob], `article-image.${ext}`, { type: blob.type });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title, text: title, url });
            return;
          }
        } catch {
          // Image share failed, fall through to text-only share
        }
      }

      await navigator.share(shareData);
    } catch (e) {
      if (e.name !== 'AbortError') console.warn('Share failed:', e);
    }
  };

  if (loading) return <><Navbar /><div className="loading-spinner">Loading article...</div></>;
  if (!article) return <><Navbar /><div className="empty-state"><h3>Article not found</h3></div></>;

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <>
      <Helmet>
        <title>{displayTitle} | NewsBuzz Today</title>
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="NewsBuzz Today" />
        <meta property="og:title" content={displayTitle} />
        <meta property="og:description" content={article.summary || displayTitle} />
        <meta property="og:image" content={article.thumbnailURL || 'https://newsbuzztoday.online/logo512.png'} />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={displayTitle} />
        <meta name="twitter:description" content={article.summary || displayTitle} />
        <meta name="twitter:image" content={article.thumbnailURL || 'https://newsbuzztoday.online/logo512.png'} />
      </Helmet>
      <Navbar />
      <main className="article-main container">
        <div className="article-layout">
          <article className="article-content">
            <div className="article-header">
              <Link to={`/category/${article.category?.toLowerCase()}`} className="badge article-cat-badge">{article.category}</Link>
              <h1 className="article-title">{displayTitle}</h1>
              <div className="article-meta">
                <span className="meta-item">✍️ <strong>{article.author}</strong></span>
                <span className="meta-item">🕐 {formatDateTime(article.createdAt)}</span>
              </div>
              {article.tags && (
                <div className="article-tags">
                  {article.tags.split(',').map(t => <span key={t} className="tag">#{t.trim()}</span>)}
                </div>
              )}
            </div>

            {availableLangs.length > 1 && (
              <div className="article-lang-switcher">
                <span className="lang-switcher-label">Read in:</span>
                {availableLangs.map(lc => {
                  const info = { english: { flag: '🇬🇧', label: 'English' }, telugu: { flag: '🇮🇳', label: 'Telugu' }, hindi: { flag: '🇮🇳', label: 'Hindi' } }[lc];
                  return (
                    <button
                      key={lc}
                      className={`lang-switch-btn ${activeLang === lc ? 'active' : ''}`}
                      onClick={() => handleLangSwitch(lc)}
                    >
                      {info?.flag} {info?.label}
                    </button>
                  );
                })}
              </div>
            )}


            {article.thumbnailURL && (
              <div className="article-hero-img">
                <img src={article.thumbnailURL} alt={displayTitle} />
              </div>
            )}

            <div className="article-body" dangerouslySetInnerHTML={{ __html: displayContent }} />

            <div className="share-section">
              <span className="share-label">Share this article:</span>

              {/* Share button — uses native share on mobile, opens WhatsApp on desktop */}
              <button className="share-btn native-share" onClick={hasNativeShare ? nativeShare : () => share('whatsapp')}>
                📤 Share
              </button>

              <div className="share-buttons">
                <button className="share-btn whatsapp" onClick={() => share('whatsapp')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg>
                  WhatsApp
                </button>
                <button className="share-btn instagram" onClick={() => share('instagram')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
                  Instagram
                </button>
                <button className="share-btn facebook" onClick={() => share('facebook')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
                  Facebook
                </button>
                <button className="share-btn twitter" onClick={() => share('twitter')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                  Twitter
                </button>
                <button className="share-btn telegram" onClick={() => share('telegram')}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: 6, verticalAlign: 'middle' }}><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.479.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" /></svg>
                  Telegram
                </button>
                <button className="share-btn copy" onClick={copyLink}>
                  {copied ? '✅ Copied!' : '🔗 Copy Link'}
                </button>
              </div>
            </div>

            {related.length > 0 && (
              <div className="related-section">
                <h3 className="section-title">Related Articles</h3>
                <div className="related-grid">
                  {related.map(a => (
                    <Link key={a.id} to={`/article/${a.id}`} className="related-card">
                      {a.thumbnailURL && <img src={a.thumbnailURL} alt={a.title} />}
                      <div className="related-info">
                        <span className="badge" style={{ fontSize: '0.62rem' }}>{a.category}</span>
                        <h4>{a.title}</h4>
                        <span className="meta-time">{formatDateTime(a.createdAt)}</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
          <div className="article-sidebar">
            <Sidebar />
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}